/** Date helpers that work in an institution's IANA timezone (default Africa/Nairobi). */

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string, opts: Intl.DateTimeFormatOptions, locale = 'en-GB') {
  const key = `${locale}|${tz}|${JSON.stringify(opts)}`;
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { timeZone: tz, ...opts });
    fmtCache.set(key, f);
  }
  return f;
}

/** YYYY-MM-DD in the given timezone. */
export function localDate(d: Date | string, tz: string): string {
  return fmt(tz, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA').format(new Date(d));
}

/** "9:02" style 24h time without leading zero on the hour (design timeline). */
export function localTime(d: Date | string, tz: string): string {
  const parts = fmt(tz, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(d));
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${h}:${m}`;
}

/** "HH:MM" zero-padded 24h (exports). */
export function localTime24(d: Date | string, tz: string): string {
  return fmt(tz, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(d));
}

/** "9:01 AM" (profile visits). */
export function localTime12(d: Date | string, tz: string): string {
  return fmt(tz, { hour: 'numeric', minute: '2-digit', hour12: true }, 'en-US').format(new Date(d));
}

/** "Monday, 26 October" */
export function longDay(d: Date | string, tz: string): string {
  return fmt(tz, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(d));
}

/** { day: '26', mon: 'OCT' } */
export function dayMon(d: Date | string, tz: string): { day: string; mon: string } {
  const [at, zone] = asInstant(d, tz);
  const parts = fmt(zone, { day: '2-digit', month: 'short' }).formatToParts(at);
  return {
    day: parts.find((p) => p.type === 'day')?.value ?? '',
    mon: (parts.find((p) => p.type === 'month')?.value ?? '').toUpperCase().slice(0, 3),
  };
}

/** A calendar date (YYYY-MM-DD) is not an instant: read it at noon UTC and format it in UTC. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const asInstant = (d: Date | string, tz: string): [Date, string] =>
  typeof d === 'string' && DATE_ONLY.test(d) ? [new Date(`${d}T12:00:00Z`), 'UTC'] : [new Date(d), tz];

/** "26 Oct" — accepts an instant or a YYYY-MM-DD date */
export function shortDate(d: Date | string, tz: string): string {
  const [at, zone] = asInstant(d, tz);
  return fmt(zone, { day: '2-digit', month: 'short' }).format(at);
}

/** Add days to a YYYY-MM-DD string. */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "Tue 27" for a YYYY-MM-DD */
export function weekdayDay(date: string): [string, string] {
  const d = new Date(`${date}T12:00:00Z`);
  const wd = d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  return [wd, String(d.getUTCDate())];
}

/** "26-Oct-2026" style used in export filenames */
export function fileDate(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const mon = d.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
  return `${String(d.getUTCDate()).padStart(2, '0')}-${mon}-${d.getUTCFullYear()}`;
}

/** Export filename span: "20-26-Oct-2026", "29-Oct-04-Nov-2026", "29-Dec-2026-04-Jan-2027". */
export function fileDateRange(from: string, to: string): string {
  if (from === to) return fileDate(to);
  const [fd, fm, fy] = fileDate(from).split('-');
  const end = fileDate(to);
  const [, tm, ty] = end.split('-');
  if (fy !== ty) return `${fileDate(from)}-${end}`;
  if (fm !== tm) return `${fd}-${fm}-${end}`;
  return `${fd}-${end}`;
}

export const MS_DAY = 86_400_000;
